/**
 * Landing-page copy and structured content.
 *
 * Every public sentence here is governed by docs/eidas-claims-policy-2026-09-11.md
 * (tier T0). Do not write "tamper-proof", "court-proof", "legally binding",
 * "legally valid", "eIDAS certified", "notarised" or "blockchain-certified".
 * Preferred wording: "sealed against alteration", "tamper-evident",
 * "independently verifiable", "finalised evidence record", "proof of time".
 */
import type { IconName } from "./icons";

export type NavItem = { label: string; href: string };

/** In-page anchors. "Verify a file" points at the verification section because
 *  the app only exposes /verify/:evidenceId — there is no generic verify route. */
export const NAV_ITEMS: readonly NavItem[] = [
  { label: "Features", href: "#features" },
  { label: "How it works", href: "#how-it-works" },
  { label: "Use cases", href: "#use-cases" },
  { label: "Verify a file", href: "#verify" },
  { label: "FAQ", href: "#faq" },
];

export type TrustItem = { icon: IconName; label: string };

export const TRUST_ITEMS: readonly TrustItem[] = [
  { icon: "lock", label: "Protected finalised package" },
  { icon: "fingerprint", label: "SHA-256 file fingerprint" },
  { icon: "chain", label: "Cryptographic evidence chain" },
  { icon: "clock", label: "Independently verifiable proof of time" },
  { icon: "noCloud", label: "No original files or personal data on Bitcoin" },
];

export type ProtectionLayer = {
  icon: IconName;
  eyebrow: string;
  title: string;
  body: string;
  outcome: string;
};

export const PROTECTION_LAYERS: readonly ProtectionLayer[] = [
  {
    icon: "lock",
    eyebrow: "Immutable",
    title: "Sealed against alteration",
    body:
      "Finalised evidence is stored in a protected package. AWS S3 Object Lock in COMPLIANCE mode prevents alteration or deletion before the retention period ends.",
    outcome: "Protected against alteration or deletion during the retention period.",
  },
  {
    icon: "chain",
    eyebrow: "Chained",
    title: "Changes become visible",
    body:
      "Each finalised record extends a cryptographic evidence chain. If one record is changed, the records that follow no longer match.",
    outcome: "Tamper-evident by design.",
  },
  {
    icon: "clock",
    eyebrow: "Anchored",
    title: "Independently time-proven",
    body:
      "A cryptographic fingerprint of the evidence manifest is anchored to Bitcoin through OpenTimestamps. This creates a proof of time that does not depend on StampmatS.",
    outcome: "Independently verifiable proof of time.",
  },
];

export type ProcessStep = {
  icon: IconName;
  title: string;
  body: string;
};

export const PROCESS_STEPS: readonly ProcessStep[] = [
  {
    icon: "upload",
    title: "Upload evidence",
    body: "Add photos, videos, or files from your phone or computer.",
  },
  {
    icon: "seal",
    title: "Finalise and seal",
    body: "The finalised evidence package is protected against alteration or deletion during the retention period.",
  },
  {
    icon: "fingerprint",
    title: "Create the evidence fingerprint",
    body: "StampmatS creates a SHA-256 fingerprint and links the record into the evidence chain.",
  },
  {
    icon: "clock",
    title: "Create an independent time proof",
    body: "A cryptographic proof is anchored to Bitcoin through OpenTimestamps. Your files and personal data stay off-chain.",
  },
  {
    icon: "search",
    title: "Verify later",
    body: "Recalculate the fingerprint, compare it with the finalised record, and check the proof of time.",
  },
];

export type VerifyPoint = { icon: IconName; label: string };

export const VERIFY_POINTS: readonly VerifyPoint[] = [
  { icon: "fingerprint", label: "The file's SHA-256 fingerprint can be recalculated at any time." },
  { icon: "file", label: "The fingerprint is compared with the finalised evidence record." },
  { icon: "chain", label: "The evidence chain shows whether earlier or later records were altered." },
  { icon: "clock", label: "The Bitcoin-anchored proof shows the evidence existed by a specific time." },
];

export type UseCase = { icon: IconName; title: string; body: string };

export const USE_CASES: readonly UseCase[] = [
  {
    icon: "home",
    title: "Property condition records",
    body: "Document the state of a property at move-in, move-out, or handover.",
  },
  {
    icon: "alert",
    title: "Incident and claim documentation",
    body: "Capture damage, incidents, and supporting files with a verifiable time proof.",
  },
  {
    icon: "clipboard",
    title: "Compliance records",
    body: "Keep verifiable records of inspections, checks, and required documentation.",
  },
  {
    icon: "flag",
    title: "Incident documentation",
    body: "Record what happened, when it was documented, and whether the record changed.",
  },
  {
    icon: "handshake",
    title: "Contract and delivery records",
    body: "Preserve proof of delivered work, signed documents, and agreed deliverables.",
  },
  {
    icon: "layers",
    title: "Audit-ready evidence trail",
    body: "Build a tamper-evident record of finalised digital evidence over time.",
  },
];

export type ProofCard = { icon: IconName; title: string; body: string };

export const PROOF_CARDS: readonly ProofCard[] = [
  {
    icon: "file",
    title: "What was finalised",
    body: "The exact file fingerprint and evidence record.",
  },
  {
    icon: "clock",
    title: "When it was finalised",
    body: "An independently verifiable proof of time.",
  },
  {
    icon: "check",
    title: "Whether it still matches",
    body: "Any later change to the file becomes detectable.",
  },
];

export type FaqItem = { question: string; answer: string };

export const FAQ_ITEMS: readonly FaqItem[] = [
  {
    question: "What does StampmatS actually prove?",
    answer:
      "StampmatS helps prove that a specific file existed in a specific form by a specific time, and whether it has changed since it was finalised.",
  },
  {
    question: "Does StampmatS prove the content of my photo or video is true?",
    answer:
      "No. StampmatS proves integrity and timing of the finalised evidence record. It does not verify what the content shows or whether the content is accurate.",
  },
  {
    question: "Are my files stored on Bitcoin?",
    answer:
      "No. Only a cryptographic proof derived from the evidence manifest is anchored. Your original files, media, and personal data are not published on Bitcoin.",
  },
  {
    question: "Can evidence be deleted or changed after it is finalised?",
    answer:
      "Finalised evidence packages are protected against alteration or deletion during the configured retention period.",
  },
  {
    question: "What happens if a file is modified later?",
    answer:
      "The modified file will produce a different SHA-256 fingerprint. It will no longer match the finalised evidence record.",
  },
  {
    question: "Can someone verify my evidence without trusting StampmatS?",
    answer:
      "The Bitcoin-anchored proof is designed to be independently verifiable, so the time proof does not depend on StampmatS alone.",
  },
  {
    question: "Is StampmatS legal evidence?",
    answer:
      "StampmatS provides technical integrity and time proofs. Whether evidence is accepted, and how much weight it carries, depends on the specific case, jurisdiction, and decision-maker.",
  },
  {
    question: "Is StampmatS eIDAS certified?",
    answer:
      "No. StampmatS does not claim to be a qualified trust service. It uses recognised cryptographic methods and is designed with EU data protection and digital evidence principles in mind.",
  },
  {
    question: "What file types can I use?",
    answer: "Photos, videos, and other digital files, depending on your plan and current upload limits.",
  },
  {
    question: "Where is my data stored?",
    answer:
      "Evidence is stored in protected cloud storage. Original files are not published to any public blockchain.",
  },
  {
    question: "How do I get started?",
    answer: "Create an account, upload your first file, and finalise it to create a verifiable evidence record.",
  },
];
